import type { Collab, CollabPreview } from "@cabal/shared/domain/session/collab/types.js";
import { SessionEntityKind } from "@cabal/shared/domain/session/enums.js";
import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import type { ReflectionPreview } from "@cabal/shared/domain/session/reflection/types.js";
import type { Member } from "@cabal/shared/domain/session/team/member/types.js";
import type { UserShortIdView } from "@cabal/shared/domain/session/user-short-id/types.js";
import type {
  VersionPreview,
  VersionSummary,
} from "@cabal/shared/domain/session/version/types.js";
import type { ShortIdView } from "@cabal/shared/domain/short-id/types.js";
import type { AttachedFileView } from "@cabal/shared/domain/upload/types.js";

export interface ShortIdLookupEntry {
  type: SessionEntityKind;
  entityId: string;
}

export interface EntityShortId {
  canonical: string;
  formatted: string;
}

export interface ShortIdIndexSources {
  products: Record<string, ProductSummary>;
  versions: Record<string, VersionSummary>;
  attachedFiles: Record<string, AttachedFileView>;
  members: Record<string, Member>;
  collabs: Record<string, Collab>;
  chatMessages: Record<string, Collab>;
  versionPreviews: Record<string, VersionPreview>;
  collabPreviews: Record<string, CollabPreview>;
  reflectionPreviews: Record<string, ReflectionPreview>;
  userShortIdViews: readonly UserShortIdView[];
}

interface ShortIdEntity {
  id: string;
  shortId: ShortIdView;
}

interface FromShortIdEntityGroup {
  type: SessionEntityKind;
  entities: readonly ShortIdEntity[];
}

export function selectFromShortId(
  sources: ShortIdIndexSources,
): Record<string, ShortIdLookupEntry> {
  const map: Record<string, ShortIdLookupEntry> = {};

  selectFromShortIdGroups(sources).forEach((group) => {
    group.entities.forEach((entity) => {
      addFromShortIdEntity({
        map,
        type: group.type,
        entity,
      });
    });
  });

  sources.userShortIdViews.forEach((view) => {
    addShortIdAliases({
      map,
      shortId: view.shortId,
      entry: {
        type: SessionEntityKind.User,
        entityId: view.userId,
      },
    });
  });

  return map;
}

export function selectToShortId(
  sources: ShortIdIndexSources,
): Record<string, EntityShortId> {
  const map: Record<string, EntityShortId> = {};

  selectToShortIdGroups(sources).forEach((entities) => {
    entities.forEach((entity) => {
      map[entity.id] = selectEntityShortId(entity.shortId);
    });
  });

  sources.userShortIdViews.forEach((view) => {
    map[view.userId] = selectEntityShortId(view.shortId);
  });

  return map;
}

function selectFromShortIdGroups(
  sources: ShortIdIndexSources,
): FromShortIdEntityGroup[] {
  return [
    {
      type: SessionEntityKind.Version,
      entities: Object.values(sources.versionPreviews),
    },
    {
      type: SessionEntityKind.Collab,
      entities: Object.values(sources.collabPreviews),
    },
    {
      type: SessionEntityKind.Reflection,
      entities: Object.values(sources.reflectionPreviews),
    },
    {
      type: SessionEntityKind.Product,
      entities: Object.values(sources.products),
    },
    {
      type: SessionEntityKind.Version,
      entities: Object.values(sources.versions),
    },
    {
      type: SessionEntityKind.File,
      entities: Object.values(sources.attachedFiles),
    },
    {
      type: SessionEntityKind.Member,
      entities: Object.values(sources.members),
    },
    {
      type: SessionEntityKind.Collab,
      entities: Object.values(sources.collabs),
    },
    {
      type: SessionEntityKind.Collab,
      entities: Object.values(sources.chatMessages),
    },
  ];
}

function selectToShortIdGroups(
  sources: ShortIdIndexSources,
): readonly ShortIdEntity[][] {
  return [
    Object.values(sources.versionPreviews),
    Object.values(sources.collabPreviews),
    Object.values(sources.reflectionPreviews),
    Object.values(sources.products),
    Object.values(sources.versions),
    Object.values(sources.attachedFiles),
    Object.values(sources.members),
    Object.values(sources.collabs),
    Object.values(sources.chatMessages),
  ];
}

function addFromShortIdEntity(input: {
  map: Record<string, ShortIdLookupEntry>;
  type: SessionEntityKind;
  entity: ShortIdEntity;
}): void {
  addShortIdAliases({
    map: input.map,
    shortId: input.entity.shortId,
    entry: {
      type: input.type,
      entityId: input.entity.id,
    },
  });
}

function addShortIdAliases(input: {
  map: Record<string, ShortIdLookupEntry>;
  shortId: ShortIdView;
  entry: ShortIdLookupEntry;
}): void {
  selectShortIdAliases(input.shortId).forEach((alias) => {
    input.map[alias] = input.entry;
  });
}

function selectShortIdAliases(shortId: ShortIdView): string[] {
  if (shortId.formatted === shortId.canonical) {
    return [shortId.canonical];
  }

  return [shortId.canonical, shortId.formatted];
}

function selectEntityShortId(shortId: ShortIdView): EntityShortId {
  return {
    canonical: shortId.canonical,
    formatted: shortId.formatted,
  };
}
